import * as XLSX from "xlsx";
import { supabase } from "../lib/supabaseClient";

export const processAndDownloadPayroll = async (
  sageTemplateData,
  payrollStart,
  payrollEnd,
) => {
  if (!sageTemplateData || sageTemplateData.length === 0) {
    throw new Error("Please upload a valid Sage template first.");
  }

  // Always compute fresh, at the exact moment of generation, directly from
  // the database - never from whatever the page happened to have loaded.
  const { data: overview, error: overviewError } = await supabase.rpc(
    "get_tutor_overview",
    { p_start: payrollStart, p_end: payrollEnd },
  );
  if (overviewError) throw overviewError;

  // Only tutors with real, outstanding earnings for this period belong in
  // the Sage payment file. Without this filter, every tutor in the system
  // (including test accounts and tutors whose hours were never approved)
  // would be written into the payment file with a stale/zero amount.
  const payableTutors = (overview || []).filter(
    (t) => Number(t.total_earnings) > 0,
  );

  // 1. Prepare Export Data
  const finalExportData = payableTutors.map((tutor) => {
    const sageMatch = sageTemplateData.find(
      (row) =>
        row["Email Address"]?.trim().toLowerCase() ===
        tutor.email?.trim().toLowerCase(),
    );

    return {
      AccRef: tutor.name,
      AccName: tutor.name,
      "Bank or CC Name": tutor.name,
      AccType: sageMatch ? sageMatch["AccType"] : "",
      BrCode: sageMatch ? sageMatch["BrCode"] : "",
      "Bank or CC Number": sageMatch ? sageMatch["Bank or CC Number"] : "",
      Amount: Number(Number(tutor.total_earnings).toFixed(2)),
      "Payment Ref": "Tutors",
      Active: "TRUE",
    };
  });

  const headers = [
    "AccRef",
    "AccName",
    "Bank or CC Name",
    "AccType",
    "BrCode",
    "Bank or CC Number",
    "Amount",
    "Payment Ref",
    "Active",
  ];

  // 2. Generate Excel
  const newSheet = XLSX.utils.json_to_sheet(finalExportData, {
    header: headers,
  });
  const newWorkbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(newWorkbook, newSheet, "Sage_Upload");
  XLSX.writeFile(
    newWorkbook,
    `Sage_Payroll_${payrollStart}_${payrollEnd}.xlsx`,
  );

  // 3. Get the exact sessions backing these totals, fresh from the DB - not
  // re-derived from the export rows above, so this can never drift from
  // what get_tutor_overview actually counted.
  const { data: sessions, error: sessionsError } = await supabase.rpc(
    "get_payroll_sessions",
    { p_start: payrollStart, p_end: payrollEnd },
  );
  if (sessionsError) throw sessionsError;

  const logIds = (sessions || []).map((s) => s.log_id);

  // Hand back the exact session rows fetched above, from BEFORE
  // finalize_payroll runs - the breakdown workbook (payrollBreakdownService)
  // formats these directly rather than re-querying get_payroll_sessions
  // itself. Re-querying afterward would filter is_paid = false against
  // sessions that finalize_payroll had just marked paid, and always come
  // back empty. `confirmed` lets the caller know whether the user actually
  // went ahead with marking sessions paid - if they cancelled, the
  // breakdown (an audit trail of what got paid) shouldn't be generated
  // even though these session rows are still returned.
  const confirmed = window.confirm(
    `Sage Payroll Sheet generated. Mark ${logIds.length} sessions as PAID?`,
  );
  const result = { updatedCount: 0, sessions: sessions || [], confirmed };

  if (confirmed && logIds.length > 0) {
    // Single atomic RPC call - finalize_payroll re-validates each session
    // still qualifies at write time and marks it paid in one transaction,
    // rather than chunking client-side updates that could partially fail.
    const { data: updatedCount, error: finalizeError } = await supabase.rpc(
      "finalize_payroll",
      { p_start: payrollStart, p_end: payrollEnd, p_log_ids: logIds },
    );
    if (finalizeError) throw finalizeError;

    if (updatedCount !== logIds.length) {
      console.warn(
        `Expected to mark ${logIds.length} sessions paid, but only ${updatedCount} still qualified - some may have changed since the file was generated.`,
      );
    }

    result.updatedCount = updatedCount;
  }

  return result;
};
